import { useMemo, useState } from "react";
import useAdminSchemas, { ADMIN_SCHEMAS_LIMIT } from "admin/hooks/useAdminSchemas";
import useAdminTables, { ADMIN_TABLES_LIMIT } from "admin/hooks/useAdminTables";
import { AdminTableAccessLevel } from "admin/types/interfaces/adminTable";
import { Eye, EyeClosed, EyeOff, Loader2, Pencil, type LucideIcon } from "lucide-react";
import { Button } from "~/components/ui/button";
import Combobox from "~/components/ui/combobox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import useToast from "~/components/ui/toast";
import { ApiError, apiMutate } from "~/utils/apiClient";

const { VITE_CATALOG_API, VITE_CUSTOMER, MODE } = import.meta.env;

const DATABASE_NAME = (
  MODE === "production" ? `${VITE_CUSTOMER}_prod` : `${VITE_CUSTOMER}_stage`
).toUpperCase();

function formatApiError(message: string): string {
  try {
    const parsed = JSON.parse(message);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return message;
  }
}

// The caller's access level on a table, shown as an icon next to it. Read/write
// reuse the icons from the User Management access panel; preview and no-access
// pick eye-family icons for the states that panel does not cover.
const ACCESS_ICON: Record<AdminTableAccessLevel | "none", { Icon: LucideIcon; label: string }> = {
  write: { Icon: Pencil, label: "Read and write" },
  read: { Icon: Eye, label: "Read" },
  preview: { Icon: EyeClosed, label: "Preview" },
  none: { Icon: EyeOff, label: "No access" },
};

const TableAccessIcon = ({ access }: { access: AdminTableAccessLevel | null }) => {
  const { Icon, label } = ACCESS_ICON[access ?? "none"];
  return (
    <span title={label} className="shrink-0 text-muted-foreground">
      <Icon aria-label={label} className="h-3.5 w-3.5" />
    </span>
  );
};

interface AddTableDialogProps {
  /** The dataset to add a table to; the dialog is open while this is non-null. */
  datasetId: string | null;
  datasetTitle: string | undefined;
  onClose: () => void;
}

/**
 * "Add Table" modal, split out from the datasets table so that typing in the
 * free-text field only re-renders this dialog — not the (up to 500-row) table.
 */
const AddTableDialog = ({ datasetId, datasetTitle, onClose }: AddTableDialogProps) => {
  const { showSuccess } = useToast();
  const [tableFqn, setTableFqn] = useState("");
  const [isAddingTable, setIsAddingTable] = useState(false);
  const [addTableError, setAddTableError] = useState<string | null>(null);

  // The free-text field (contract: "SCHEMA.TABLE") is the single source of truth.
  // Split it on the first dot; the dropdown selections are derived from these parts,
  // so typing and picking stay in sync both ways with no effects.
  const dotIndex = tableFqn.indexOf(".");
  const schemaPart = dotIndex === -1 ? tableFqn : tableFqn.slice(0, dotIndex);
  const tablePart = dotIndex === -1 ? "" : tableFqn.slice(dotIndex + 1);

  // Schemas load only while the dialog is open; tables load only once the schema
  // part names a real schema (the endpoint requires one).
  const {
    items: schemas,
    metadata: schemasMetadata,
    isLoading: isLoadingSchemas,
  } = useAdminSchemas({ enabled: datasetId !== null });
  // Membership sets + memoized option lists keep per-keystroke work O(1) rather
  // than rescanning/rebuilding these (up to 5000-item) lists on every keystroke.
  const schemaNames = useMemo(() => new Set(schemas.map((schema) => schema.name)), [schemas]);
  const selectedSchema = schemaNames.has(schemaPart) ? schemaPart : null;

  const {
    items: tables,
    metadata: tablesMetadata,
    isLoading: isLoadingTables,
  } = useAdminTables(selectedSchema);
  const tableNames = useMemo(() => new Set(tables.map((table) => table.name)), [tables]);
  const selectedTable = tableNames.has(tablePart) ? tablePart : null;

  const schemaOptions = useMemo(
    () => schemas.map((schema) => ({ value: schema.name, label: schema.name })),
    [schemas]
  );
  const tableOptions = useMemo(
    () =>
      tables.map((table) => ({
        value: table.name,
        label: table.name,
        icon: <TableAccessIcon access={table.access_level} />,
      })),
    [tables]
  );

  const handleClose = () => {
    setTableFqn("");
    setAddTableError(null);
    onClose();
  };

  // Dropdown picks just rewrite the free-text field; the derivation above then
  // reflects them back as the selected values.
  const handleSelectSchema = (schema: string) => {
    setTableFqn(`${schema}.`);
  };

  const handleSelectTable = (table: string) => {
    setTableFqn(`${schemaPart}.${table}`);
  };

  const handleAddTable = async () => {
    if (!datasetId || !tableFqn.trim()) return;
    setIsAddingTable(true);
    setAddTableError(null);
    try {
      await apiMutate(`${VITE_CATALOG_API}/admin/datasets/${datasetId}/tables`, "POST", {
        table_fqn: `snowflake.${DATABASE_NAME}.${tableFqn.trim()}`,
      });
      showSuccess("Table added successfully");
      handleClose();
    } catch (err) {
      const detail =
        err instanceof ApiError ? formatApiError(err.message) : "An unexpected error occurred";
      setAddTableError(detail);
    } finally {
      setIsAddingTable(false);
    }
  };

  return (
    <Dialog open={datasetId !== null} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Table</DialogTitle>
          <DialogDescription>
            <span className="font-semibold">{datasetTitle}</span>
            <span className="font-mono text-xs ml-1.5">({datasetId})</span>
          </DialogDescription>
          <DialogDescription>
            Pick a schema and table from the dropdowns, or type them directly below. The database
            prefix <span className="font-mono font-semibold">snowflake.{DATABASE_NAME}</span> will
            be added automatically.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Combobox
            options={schemaOptions}
            value={selectedSchema}
            onChange={handleSelectSchema}
            placeholder="Select a schema"
            searchPlaceholder="Search schemas..."
            emptyText="No schemas found."
            loading={isLoadingSchemas}
          />
          {schemasMetadata?.has_more && (
            <p className="text-xs text-muted-foreground">
              Showing the first {ADMIN_SCHEMAS_LIMIT} schemas. Type below if a schema is missing.
            </p>
          )}
          <Combobox
            options={tableOptions}
            value={selectedTable}
            onChange={handleSelectTable}
            placeholder={selectedSchema ? "Select a table" : "Select a schema first"}
            searchPlaceholder="Search tables..."
            emptyText="No tables found."
            disabled={!selectedSchema}
            loading={isLoadingTables}
          />
          {tablesMetadata?.has_more && (
            <p className="text-xs text-muted-foreground">
              Showing the first {ADMIN_TABLES_LIMIT} tables. Type below if a table is missing.
            </p>
          )}
        </div>
        <Input
          className="mt-2"
          placeholder="e.g. RAW.MYTABLE"
          value={tableFqn}
          onChange={(e) => setTableFqn(e.target.value)}
        />
        {addTableError && (
          <p className="text-sm text-destructive whitespace-pre-wrap break-all">{addTableError}</p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isAddingTable}>
            Cancel
          </Button>
          <Button onClick={handleAddTable} disabled={!tableFqn.trim() || isAddingTable}>
            {isAddingTable ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddTableDialog;
